/**
 * WelcomeSaleBanner – Premium floating banner for the 45-minute Welcome Sale.
 *
 * Shows a visually impactful countdown with 25% off messaging.
 * Appears on every page while the sale is active and not dismissed.
 * Clicking the CTA navigates to the pricing page.
 */
import { motion, AnimatePresence } from 'framer-motion';
import { useWelcomeSale } from '../contexts/WelcomeSaleContext';
import { trackEvent } from '../utils/analytics';
import { Gift, Sparkles, Clock, ArrowRight, PartyPopper } from 'lucide-react';

const pad = (n: number) => String(n).padStart(2, '0');

export default function WelcomeSaleBanner() {
  const { isActive, minutesLeft, secondsLeft, isDismissed, dismiss } = useWelcomeSale();

  const visible = isActive && !isDismissed;

  // Urgency states for color escalation
  const isUrgent = minutesLeft < 10;
  const isCritical = minutesLeft < 5;

  const handleGoToPricing = () => {
    trackEvent('welcome_sale_cta_clicked', {
      minutes_remaining: minutesLeft,
      seconds_remaining: secondsLeft,
    });
    window.dispatchEvent(
      new CustomEvent('navigateToPage', { detail: { page: 'pricing' } })
    );
  };

  const handleDismiss = () => {
    trackEvent('welcome_sale_dismissed', {
      minutes_remaining: minutesLeft,
    });
    dismiss();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="welcome-sale-banner"
          initial={{ opacity: 0, y: 80, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 60, scale: 0.95 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[999] w-[95vw] max-w-[680px]"
        >
          {/* Outer glow layer */}
          <motion.div
            className="absolute inset-0 rounded-2xl pointer-events-none"
            animate={{
              boxShadow: isCritical
                ? [
                    '0 0 30px rgba(239,68,68,0.25), 0 8px 32px rgba(0,0,0,0.12)',
                    '0 0 50px rgba(239,68,68,0.4), 0 8px 32px rgba(0,0,0,0.12)',
                    '0 0 30px rgba(239,68,68,0.25), 0 8px 32px rgba(0,0,0,0.12)',
                  ]
                : isUrgent
                ? [
                    '0 0 30px rgba(245,158,11,0.2), 0 8px 32px rgba(0,0,0,0.12)',
                    '0 0 45px rgba(245,158,11,0.35), 0 8px 32px rgba(0,0,0,0.12)',
                    '0 0 30px rgba(245,158,11,0.2), 0 8px 32px rgba(0,0,0,0.12)',
                  ]
                : [
                    '0 0 30px rgba(168,85,247,0.15), 0 8px 32px rgba(0,0,0,0.12)',
                    '0 0 50px rgba(168,85,247,0.25), 0 8px 32px rgba(0,0,0,0.12)',
                    '0 0 30px rgba(168,85,247,0.15), 0 8px 32px rgba(0,0,0,0.12)',
                  ],
            }}
            transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          />

          {/* Main card */}
          <div
            className={`relative overflow-hidden rounded-2xl backdrop-blur-2xl border transition-colors duration-500 ${
              isCritical
                ? 'bg-red-50/95 dark:bg-red-950/90 border-red-300 dark:border-red-500/40'
                : isUrgent
                ? 'bg-amber-50/95 dark:bg-amber-950/90 border-amber-300 dark:border-amber-500/40'
                : 'bg-white/95 dark:bg-[#120e1e]/95 border-purple-200 dark:border-purple-500/30'
            }`}
          >
            {/* Shimmer sweep */}
            <motion.div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.08) 50%, transparent 70%)',
                backgroundSize: '200% 100%',
              }}
              animate={{ x: ['-100%', '200%'] }}
              transition={{ repeat: Infinity, duration: 3, ease: 'linear', repeatDelay: 2 }}
            />

            {/* Confetti-like top accent bar */}
            <div
              className={`h-1 w-full transition-all duration-500 ${
                isCritical
                  ? 'bg-gradient-to-r from-red-400 via-red-500 to-orange-400'
                  : isUrgent
                  ? 'bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500'
                  : 'bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-400'
              }`}
            />

            {/* Dismiss button */}
            <button
              onClick={handleDismiss}
              className="absolute top-3 right-3 z-20 w-7 h-7 rounded-full bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 border border-black/10 dark:border-white/10 flex items-center justify-center text-text-muted hover:text-text-main transition-all cursor-pointer"
              aria-label="Dismiss welcome sale"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Content */}
            <div className="relative z-10 px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center gap-4">
              {/* Left: Icon + text */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {/* Animated gift icon */}
                <div className="relative shrink-0">
                  <motion.div
                    animate={{
                      scale: [1, 1.12, 1],
                      rotate: [0, -5, 5, 0],
                    }}
                    transition={{
                      repeat: Infinity,
                      duration: 2,
                      ease: 'easeInOut',
                    }}
                    className={`w-11 h-11 rounded-xl flex items-center justify-center shadow-lg transition-colors duration-500 ${
                      isCritical
                        ? 'bg-gradient-to-br from-red-500 to-orange-500'
                        : isUrgent
                        ? 'bg-gradient-to-br from-amber-500 to-orange-500'
                        : 'bg-gradient-to-br from-purple-500 to-pink-500'
                    }`}
                  >
                    {isCritical ? (
                      <Clock className="w-5 h-5 text-white" />
                    ) : (
                      <Gift className="w-5 h-5 text-white" />
                    )}
                  </motion.div>

                  {/* Sparkle particles */}
                  {!isCritical && (
                    <motion.div
                      className="absolute -top-1 -right-1"
                      animate={{ scale: [0.8, 1.2, 0.8], opacity: [0.5, 1, 0.5] }}
                      transition={{ repeat: Infinity, duration: 1.5 }}
                    >
                      <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                    </motion.div>
                  )}
                </div>

                {/* Text block */}
                <div className="flex flex-col min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`text-[9px] sm:text-[10px] uppercase tracking-[0.2em] font-black flex items-center gap-1.5 transition-colors duration-500 ${
                        isCritical
                          ? 'text-red-600 dark:text-red-400'
                          : isUrgent
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-purple-600 dark:text-purple-400'
                      }`}
                    >
                      <span className="relative flex h-2 w-2">
                        <span
                          className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                            isCritical
                              ? 'bg-red-400'
                              : isUrgent
                              ? 'bg-amber-400'
                              : 'bg-purple-400'
                          }`}
                        />
                        <span
                          className={`relative inline-flex rounded-full h-2 w-2 ${
                            isCritical ? 'bg-red-500' : isUrgent ? 'bg-amber-500' : 'bg-purple-500'
                          }`}
                        />
                      </span>
                      {isCritical ? 'Hurry! Almost Gone' : 'Welcome Offer · Exclusive'}
                    </span>
                  </div>

                  <span className="text-sm sm:text-base font-extrabold text-text-main leading-tight mt-0.5 flex items-center gap-1.5">
                    <PartyPopper className="w-4 h-4 shrink-0 text-amber-500 hidden sm:inline" />
                    25% Off All Passes
                  </span>

                  <span className="text-[10px] sm:text-[11px] text-text-muted font-medium mt-0.5">
                    {isCritical
                      ? 'Your exclusive new-member discount expires in minutes!'
                      : 'One-time welcome discount · valid for 45 minutes only'}
                  </span>
                </div>
              </div>

              {/* Center: Countdown */}
              <div className="flex items-center gap-1.5 shrink-0">
                {[pad(minutesLeft), pad(secondsLeft)].map((val, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <div
                      className={`flex flex-col items-center px-2.5 py-1.5 rounded-lg border shadow-sm transition-colors duration-500 ${
                        isCritical
                          ? 'bg-red-100/90 dark:bg-red-900/50 border-red-200 dark:border-red-500/30'
                          : isUrgent
                          ? 'bg-amber-100/90 dark:bg-amber-900/50 border-amber-200 dark:border-amber-500/30'
                          : 'bg-purple-50/80 dark:bg-purple-900/30 border-purple-200 dark:border-purple-500/20'
                      }`}
                      style={{ minWidth: '2.6rem' }}
                    >
                      <motion.span
                        key={val}
                        initial={{ y: -6, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        className={`text-xl sm:text-2xl font-black font-mono leading-none transition-colors duration-500 ${
                          isCritical
                            ? 'text-red-600 dark:text-red-400'
                            : isUrgent
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-purple-700 dark:text-purple-300'
                        }`}
                      >
                        {val}
                      </motion.span>
                      <span className="text-[7px] text-text-muted font-bold uppercase tracking-widest mt-0.5">
                        {['MIN', 'SEC'][i]}
                      </span>
                    </div>
                    {i < 1 && (
                      <motion.span
                        animate={{ opacity: [1, 0.3, 1] }}
                        transition={{ repeat: Infinity, duration: 1 }}
                        className="text-lg font-black text-text-muted/40 -mt-3"
                      >
                        :
                      </motion.span>
                    )}
                  </div>
                ))}
              </div>

              {/* Right: CTA */}
              <motion.button
                onClick={handleGoToPricing}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                className={`shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm uppercase tracking-wider text-white shadow-lg transition-all duration-300 cursor-pointer ${
                  isCritical
                    ? 'bg-gradient-to-r from-red-500 to-orange-500 shadow-red-500/30 hover:shadow-red-500/40'
                    : isUrgent
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 shadow-amber-500/30 hover:shadow-amber-500/40'
                    : 'bg-gradient-to-r from-purple-600 to-pink-500 shadow-purple-500/30 hover:shadow-purple-500/40'
                }`}
              >
                Claim 25% Off
                <ArrowRight className="w-4 h-4" />
              </motion.button>
            </div>

            {/* Bottom progress bar showing time remaining */}
            <div className="h-0.5 w-full bg-black/5 dark:bg-white/5">
              <motion.div
                className={`h-full transition-colors duration-500 ${
                  isCritical
                    ? 'bg-gradient-to-r from-red-500 to-orange-400'
                    : isUrgent
                    ? 'bg-gradient-to-r from-amber-500 to-orange-400'
                    : 'bg-gradient-to-r from-purple-500 to-pink-500'
                }`}
                style={{
                  width: `${((minutesLeft * 60 + secondsLeft) / (45 * 60)) * 100}%`,
                }}
                transition={{ duration: 1, ease: 'linear' }}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
